// -----------------------------------------------------------------------
// <copyright company="Microsoft Corporation">
// Copyright (c) Microsoft Corporation. All rights reserved.
// </copyright>
// -----------------------------------------------------------------------

import { OnePlayerError } from '../common/onePlayerError';
import type { AuthType, ITokenRetriever } from '../types/index';

export async function webinarNavigateIframeToEmbedAsync(
  iframe: HTMLIFrameElement,
  embedSrc: string,
  tokenRetriever: ITokenRetriever
): Promise<void> {
  let accessToken: string;
  let authType: AuthType = 'Bearer';
  try {
    if (!tokenRetriever.getTokenWithAuthTypeAsync) {
      throw new Error('getTokenWithAuthTypeAsync in ITokenRetriever is not implemented.');
    }
    const tokenResult = await tokenRetriever.getTokenWithAuthTypeAsync('SPO', embedSrc);
    accessToken = tokenResult?.token;
    if (tokenResult?.authType) {
      authType = tokenResult.authType;
    }
  } catch (error: unknown) {
    throw OnePlayerError.createGenericError('fatal', 'loadIframe', 'getTokenFailed', error);
  }

  return new Promise((resolve, reject) => {
    iframe.onload = () => {
      resolve();
    };
    iframe.onerror = (error) => {
      reject(error);
    };

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const root = iframe.contentDocument!;
    if (!root.body) {
      root.createElement('body');
    }

    // Create a form that will post to the embed url
    const form = root.createElement('form');
    form.action = embedSrc;
    form.method = 'POST';
    form.style.visibility = 'hidden';
    form.target = '_self';

    // Create a hidden input element to contain the access_token
    const input = root.createElement('input');
    input.type = 'hidden';
    input.name = authType === 'Badger' ? 'badger_token' : 'access_token';
    input.value = accessToken;
    form.appendChild(input);

    // Create a hidden input element to contain the additional encryption key (playback token)
    let additionalEncryptionKey: string | undefined;
    const searchParams = new URL(embedSrc).searchParams;
    if (searchParams.has('playbackToken')) {
      additionalEncryptionKey = searchParams.get('playbackToken')?.trim() || undefined;
    }
    if (additionalEncryptionKey) {
      const input2 = root.createElement('input');
      input2.type = 'hidden';
      input2.name = 'playbackToken';
      input2.value = additionalEncryptionKey;
      form.appendChild(input2);
    }

    // Add the form to the iframe and submit it, which will cause the iframe to navigate to the embed URL
    root.body.appendChild(form);
    form.submit();
  });
}
